import { useMemo, useState, useEffect, useRef } from 'react';
import type { ControllerRenderProps } from 'react-hook-form';
import type { PolicySetting } from '@kirin/api-client/contracts/policies';
import type { SeverityType } from '@kirin/api-client/contracts/shared/severity';
import type { PolicySettingsFormData } from '../types';
import { getArrayObjectComponentType } from '../helpers/fieldDerivation';
import {
  shouldUsePatternModal,
  getPatternModalDescription,
} from '../helpers/patternModalRegistry';
import { StringArrayInput } from './StringArrayInput';
import { ArrayObjectSetting } from './ArrayObjectSetting';
import { CustomCapabilityRulesLink } from './CustomCapabilityRulesLink';
import { PatternArrayInput } from './PatternArrayInput';
import { PatternArrayModalField } from './PatternArrayModalField';
import { ExtensionVersionListField } from './ExtensionVersionListField';

interface ArraySettingFieldProps {
  setting: PolicySetting;
  policyKey: string;
  field: ControllerRenderProps<PolicySettingsFormData, string>;
  readOnly?: boolean;
  /** Support role carve-out, forwarded only to the pattern-modal field. */
  isSupportReadOnly?: boolean;
  isDirty?: boolean;
}

/**
 * Array setting field component
 * Renders different array input components based on item type and validation structure
 * Derives component type from validation.properties (schema-based)
 *
 * @param setting - The policy setting configuration
 * @param policyKey - The policy key for fallback component type detection
 * @param field - The react-hook-form field controller props
 * @param readOnly - Whether the field is read-only (pattern modals still open for viewing)
 */
export function ArraySettingField({
  setting,
  policyKey,
  field,
  readOnly = false,
  isSupportReadOnly = false,
  isDirty = false,
}: ArraySettingFieldProps) {
  // Ensure value is an array (fallback to empty array if undefined/null)
  // Memoize to avoid unnecessary re-renders
  const value = useMemo(
    () => (Array.isArray(field.value) ? field.value : []),
    [field.value]
  );

  // Track baseline for change indicators (for pattern arrays with modal)
  // Baseline represents the original/saved state to compare against current value
  const [baseline, setBaseline] = useState(value);
  const prevIsDirtyRef = useRef(isDirty);

  // Update baseline when form transitions from dirty to clean (after save)
  // This ensures change indicators clear after save
  useEffect(() => {
    // Only track baseline for object arrays (patterns)
    if (
      setting.validation?.arrayItemType === 'object' &&
      Array.isArray(value)
    ) {
      // Detect transition from dirty → clean (form was saved)
      const wasDirty = prevIsDirtyRef.current;
      const isNowClean = !isDirty;

      if (wasDirty && isNowClean && value !== baseline) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setBaseline(value);
      }

      prevIsDirtyRef.current = isDirty;
    }
  }, [isDirty, value, baseline, setting.validation?.arrayItemType]);

  // String array: render simple string list input component
  if (setting.validation?.arrayItemType === 'string') {
    if (
      policyKey === 'extensions-management' &&
      (setting.key === 'allowedExtensions' ||
        setting.key === 'blockedExtensions')
    ) {
      return (
        <ExtensionVersionListField
          value={value as string[]}
          onChange={field.onChange}
          disabled={readOnly}
          settingKey={setting.key}
        />
      );
    } else {
      return (
        <StringArrayInput
          value={value as string[]}
          onChange={field.onChange}
          placeholder={`Add ${setting.name.toLowerCase()}...`}
          settingKey={setting.key}
          policyKey={policyKey}
          validation={setting.validation}
          disabled={readOnly}
        />
      );
    }
  }

  // Object array: derive component type from validation.properties structure
  // Falls back to policy key for edge cases
  if (setting.validation?.arrayItemType === 'object') {
    // Custom capability rules are edited on their own dedicated page.
    if (setting.key === 'customCapabilityRules') {
      return (
        <CustomCapabilityRulesLink count={value.length} readOnly={readOnly} />
      );
    }

    const componentType = getArrayObjectComponentType(setting, policyKey);

    if (componentType === 'array-object') {
      // extension-security style: objects with optional fields (permission, pattern, etc.)
      // Derived from validation.properties having permission field
      return (
        <ArrayObjectSetting
          setting={setting}
          value={
            value as Array<{
              pattern?: string;
              permission?: string;
              reason?: string;
              severity?: string;
              [key: string]: unknown;
            }>
          }
          onChange={field.onChange}
          disabled={readOnly}
        />
      );
    }

    // Pattern-based style: objects with required pattern field
    // Derived from validation.properties having required pattern field
    // Used for policies like mcp-security, cursor-database, secret-detection, etc.

    if (shouldUsePatternModal(policyKey, setting.key)) {
      return (
        <PatternArrayModalField
          value={
            value as Array<{
              pattern: string;
              reason?: string;
              description?: string;
              severity: SeverityType;
            }>
          }
          onChange={field.onChange}
          readOnly={readOnly}
          isSupportReadOnly={isSupportReadOnly}
          policyKey={policyKey}
          policyName={setting.name}
          policyDescription={getPatternModalDescription(
            policyKey,
            setting.key,
            setting.description ?? ''
          )}
          baseline={
            baseline as Array<{
              pattern: string;
              reason?: string;
              description?: string;
              severity: SeverityType;
            }>
          }
        />
      );
    }

    return (
      <PatternArrayInput
        value={
          value as Array<{
            pattern: string;
            reason?: string;
            description?: string;
            severity: SeverityType;
          }>
        }
        onChange={field.onChange}
        validation={setting.validation}
        disabled={readOnly}
        policyKey={policyKey}
        settingKey={setting.key}
      />
    );
  }

  return null;
}
